import { useState, useCallback, useMemo, useContext, useEffect, useRef } from "react";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  TextField,
  DialogActions,
  Button,
} from "@mui/material";
import { RootContext } from "../context/root-provider";
import AppBackService from "../services/appback";

const styles = {
  deleteBtnStyles: {
    color: "var(--color-error)",
  },
};
// `note` is an extra line under "cannot be undone", for deletes that keep
// something the user might fear goes with it.
function useDeleteConfirm(idKey, service, resolveDelete, noun = "Document", note = null) {
  const [deleting, setDeleting] = useState(null); // set with object containing <idKey> and title
  const [pendingId, setPendingId] = useState(null); // the id whose DELETE is in flight (TT-1067)
  const { userInfo } = useContext(RootContext);

  // The request is awaited here, with the dialog held open and its buttons
  // disabled until it resolves. Before TT-1067 the dialog closed in the same
  // tick the request was fired, so for the whole round trip (ACL + tapholder
  // checks, cleanup, the delete, a possible cold start) the row looked
  // untouched and a second click was a second DELETE. Optimistic removal was
  // rejected on purpose: the delete can legitimately fail on the tapholder
  // check, and un-deleting a row the user watched vanish is the worse surprise.
  const handleDelete = useCallback(
    async (id) => {
      if (pendingId) return; // one delete at a time; ignore backdrop/Esc/re-clicks
      if (!id) {
        setDeleting(null);
        return;
      }
      setPendingId(id);
      try {
        const resp = await AppBackService[service](userInfo?.username, id);
        // the response rides along for callers that word a failure by status
        resolveDelete(!resp || resp.error ? null : id, resp);
      } finally {
        setPendingId(null);
        setDeleting(null);
      }
    },
    [pendingId, resolveDelete, service, userInfo?.username]
  );

  const deleteConfirm = useMemo(
    () =>
      deleting && (
        <Dialog open={!!deleting[idKey]} onClose={() => handleDelete(null)}>
          <DialogTitle>Delete {noun}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the {noun}
              {deleting.title ? ` "${deleting.title}"` : ""}?
              <br />
              This action cannot be undone.
              {note && (
                <>
                  <br />
                  {note}
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button disabled={!!pendingId} onClick={() => handleDelete(null)}>
              Cancel
            </Button>
            <Button
              sx={styles.deleteBtnStyles}
              disabled={!!pendingId}
              startIcon={pendingId ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={() => handleDelete(deleting[idKey])}>
              {pendingId ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      ),
    [deleting, handleDelete, idKey, note, noun, pendingId]
  );

  return { setDeleting, deleteConfirm };
}

export function useDocumentActions(updateTitle, resolveDelete) {
  const [editingTitle, setEditingTitle] = useState(null); // set with object containing docId and title
  const [title, setTitle] = useState("");
  const [titleInvalid, setTitleInvalid] = useState(null);
  const { userInfo } = useContext(RootContext);

  useEffect(() => {
    setTitle(editingTitle?.title || "");
  }, [editingTitle]);

  const { setDeleting, deleteConfirm } = useDeleteConfirm("docId", "deleteUserDoc", resolveDelete);

  const saveTitle = useCallback(
    async (docId, newTitle) => {
      const resp = await AppBackService.updateUserDoc(userInfo?.username, docId, { title: newTitle });
      updateTitle(!resp || resp.error ? null : newTitle, docId);
    },
    [updateTitle, userInfo]
  );

  const handleTitle = useCallback(
    (docId, newTitle) => {
      if (newTitle) {
        if (!/^[A-Za-z0-9\s'"_@./#&+-]*$/.test(newTitle)) {
          setTitleInvalid("Only letters and numbers allowed");
        } else {
          if (newTitle !== editingTitle.title) {
            saveTitle(docId, newTitle);
          }
          setTitleInvalid(null);
          setEditingTitle(null);
          setTitle("");
        }
      } else if (newTitle === "") {
        setTitleInvalid("Please enter a title");
      } else {
        setTitleInvalid(null);
        setEditingTitle(null);
        setTitle("");
      }
    },
    [editingTitle, saveTitle]
  );

  const editTitleDialog = useMemo(
    () =>
      editingTitle && (
        <Dialog
          fullWidth
          maxWidth="xs"
          open={!!(editingTitle.docId && editingTitle.title)}
          onClose={() => handleTitle(null)}>
          <DialogTitle>Edit Document Title</DialogTitle>
          <DialogContent>
            <DialogContentText>Enter a new title for the document "{editingTitle.title}".</DialogContentText>
            <TextField
              error={!!titleInvalid}
              helperText={titleInvalid}
              autoFocus
              margin="dense"
              type="text"
              fullWidth
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleTitle(editingTitle.docId, title);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleTitle(null)}>Cancel</Button>
            <Button variant="contained" onClick={() => handleTitle(editingTitle.docId, title)}>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      ),
    [editingTitle, handleTitle, title, titleInvalid]
  );

  return { setDeleting, deleteConfirm, setEditingTitle, editTitleDialog };
}

export function useTapActions(resolveDelete) {
  // Same dialog, same in-flight handling as documents (TT-1067).
  const { setDeleting, deleteConfirm } = useDeleteConfirm("tapId", "deleteUserTap", resolveDelete, "Tapestry");
  return { setDeletingTap: setDeleting, deleteTapConfirm: deleteConfirm };
}

export function useSourceAudioActions(resolveDelete) {
  // A doc's retained audio (TT-1190): same dialog and in-flight handling, keyed by the doc.
  const { setDeleting, deleteConfirm } = useDeleteConfirm(
    "docId",
    "deleteDocSourceAudio",
    resolveDelete,
    "Audio Recording",
    "The document and its Tapestries stay."
  );
  return { setDeletingAudio: setDeleting, deleteAudioConfirm: deleteConfirm };
}

export function useSourceAudioPlayback(docId) {
  // Playback of a doc's retained audio (TT-1190): the URL is fetched only when
  // the user asks to play, since it is signed and short-lived. It comes with
  // the doc read's audio (TT-1273: `source.audio`, signed for the owner). A URL that
  // lapses mid-listen (the player errors) is fetched again once, resuming where
  // it stopped; once the fresh URL loads, a later lapse gets the same one retry.
  // A failure with no recovery in between is reported, the player goes away
  // and Play comes back.
  const { userInfo } = useContext(RootContext);
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const resumeAt = useRef(0);
  const refetched = useRef(false);

  // Another doc's recording never plays under this one: switching docs resets
  // the player, and a URL that arrives for the doc we left is dropped.
  const currentDoc = useRef(docId);
  useEffect(() => {
    currentDoc.current = docId;
    setSrc(null);
    setState("idle");
  }, [docId]);

  const fetchUrl = useCallback(async () => {
    setState("loading");
    const resp = await AppBackService.getSpokenDoc(userInfo?.username, docId);
    if (currentDoc.current !== docId) return;
    const audio = resp?.error ? null : resp?.source?.audio;
    if (audio?.status !== "ready" || !audio?.url) {
      setSrc(null);
      setState("error");
      return;
    }
    setSrc(audio.url);
    setState("ready");
  }, [docId, userInfo?.username]);

  const play = useCallback(() => {
    refetched.current = false;
    resumeAt.current = 0;
    fetchUrl();
  }, [fetchUrl]);

  const onError = useCallback(
    (event) => {
      if (refetched.current) {
        setSrc(null);
        setState("error");
        return;
      }
      refetched.current = true;
      resumeAt.current = event?.currentTarget?.currentTime || 0;
      fetchUrl();
    },
    [fetchUrl]
  );

  const onLoadedMetadata = useCallback((event) => {
    refetched.current = false; // this URL works: the next lapse may retry
    if (resumeAt.current > 0 && event?.currentTarget) {
      event.currentTarget.currentTime = resumeAt.current;
      resumeAt.current = 0;
    }
  }, []);

  return { src, state, play, onError, onLoadedMetadata };
}
